import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ChartBar,
  Eye,
  MapPin,
  NavigationArrow,
  Image as ImageIcon,
  ShareNetwork,
  CalendarBlank,
  Sparkle,
  ArrowsClockwise,
  ArrowUp,
  ArrowDown,
  Minus,
  CaretDown,
  Check,
  MagnifyingGlass,
  Crosshair,
  Lightning,
} from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";

// Metric → label + icon. Order = display order in the comparison grid.
const METRICS = [
  { key: "pin_impression",  label: "Pin Impressions",   icon: Crosshair },
  { key: "pin_click",       label: "Pin Clicks",        icon: MapPin },
  { key: "ctr_pct",         label: "CTR",               icon: ChartBar, isPercent: true },
  { key: "profile_view",    label: "Profile Views",     icon: Eye },
  { key: "menu_view",       label: "Menu Clicks",       icon: ImageIcon },
  { key: "directions_click",label: "Direction Taps",    icon: NavigationArrow },
  { key: "share_view",      label: "Shares",            icon: ShareNetwork },
  { key: "events_posted",   label: "Events Posted",     icon: CalendarBlank },
  { key: "avg_posting_days",label: "Posting Frequency", icon: CalendarBlank, isDays: true },
];

const RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "7 days" },
  { key: "30d",   label: "30 days" },
];

function fmt(v, isPercent = false, isDays = false) {
  if (v === null || v === undefined) return "—";
  if (isPercent) return `${v}%`;
  if (isDays) return `${v} ${v === 1 ? "day" : "days"}`;
  if (typeof v === "number" && !Number.isInteger(v)) return v.toFixed(2);
  return v;
}

function pickCohortValue(cohort, key) {
  // Pin impressions/clicks/etc. live under `avg` for per-truck averages.
  // CTR + posting frequency live at the cohort root. CTR can be null
  // when no impressions exist in the window — render "—" instead of 0%.
  if (!cohort) return null;
  if (key === "ctr_pct") return cohort.ctr_pct;
  if (key === "avg_posting_days") return cohort.avg_posting_days ?? 0;
  return cohort.avg?.[key] ?? 0;
}

function pickCohortTotal(cohort, key) {
  if (!cohort) return 0;
  if (key === "ctr_pct" || key === "avg_posting_days") return null;
  return cohort.totals?.[key] ?? 0;
}

export default function AdminMarketplaceAnalyticsCard() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Boost ROI tile — event-level "with boost vs without boost" CTR
  // comparison. Loads in parallel with the cohort grid and shares the
  // same `range` selector. Null while loading, falsy on error.
  const [boostImpact, setBoostImpact] = useState(null);

  // Truck picker state
  const [trucks, setTrucks] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [truckDetail, setTruckDetail] = useState(null);
  const [truckLoading, setTruckLoading] = useState(false);

  const loadCohort = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await api.get(`/admin/marketplace-analytics?range=${range}`);
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load marketplace analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadTrucks = async () => {
    try {
      const res = await api.get("/admin/marketplace-trucks");
      setTrucks(res.data?.trucks || []);
    } catch {
      // silent — picker just shows empty state
    }
  };

  const loadBoostImpact = async () => {
    try {
      const res = await api.get(`/admin/marketplace-boost-impact?range=${range}`);
      setBoostImpact(res.data);
    } catch {
      // Boost tile is non-blocking — silently fall back to "no data"
      setBoostImpact(null);
    }
  };

  useEffect(() => { loadCohort(); loadBoostImpact(); /* eslint-disable-next-line */ }, [range]);
  useEffect(() => { loadTrucks(); }, []);

  // Re-fetch the single-truck drilldown whenever truck OR range changes.
  useEffect(() => {
    if (!selectedTruck) {
      setTruckDetail(null);
      return;
    }
    let cancelled = false;
    setTruckLoading(true);
    api.get(`/admin/marketplace-truck/${selectedTruck.truck_id}?range=${range}`)
      .then((res) => { if (!cancelled) setTruckDetail(res.data); })
      .catch((e) => {
        if (!cancelled) {
          toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load truck analytics");
          setTruckDetail(null);
        }
      })
      .finally(() => { if (!cancelled) setTruckLoading(false); });
    return () => { cancelled = true; };
  }, [selectedTruck, range]);

  const pro = data?.cohorts?.pro;
  const reg = data?.cohorts?.regular;
  const lift = data?.cohorts?.lift_pct || {};

  const proTrucks = useMemo(() => trucks.filter((t) => t.is_pro), [trucks]);
  const regTrucks = useMemo(() => trucks.filter((t) => !t.is_pro), [trucks]);

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5 sm:p-6"
      data-testid="admin-marketplace-analytics-card"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
            <ChartBar weight="fill" className="h-3.5 w-3.5" />
            Marketplace Intelligence
          </div>
          <h2 className="mt-1 font-display font-bold text-2xl tracking-tight">
            Pro vs Regular truck performance
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Is the Pro tier actually moving the needle? Compare cohorts or drill into one truck.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div
            className="inline-flex rounded-full border border-border bg-background p-0.5"
            data-testid="mkt-range-toggle"
          >
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                className={
                  "h-8 px-3 rounded-full text-xs font-medium transition " +
                  (range === r.key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground")
                }
                data-testid={`mkt-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => loadCohort({ silent: true })}
            disabled={loading || refreshing}
            className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50"
            aria-label="Refresh"
            data-testid="mkt-refresh-btn"
          >
            <ArrowsClockwise weight="duotone" className={"h-4 w-4 " + (refreshing ? "animate-spin" : "")} />
          </button>
        </div>
      </header>

      {/* Cohort header strip: total trucks per cohort. Pro on the LEFT
          intentionally — Pro is the headline tier and the lift story
          reads more naturally Pro→Regular. */}
      <div className="grid gap-2.5 mt-4 grid-cols-2">
        <CohortHeaderTile cohort={pro} label="Pro" tone="primary" loading={loading} />
        <CohortHeaderTile cohort={reg} label="Regular" tone="muted" loading={loading} />
      </div>

      {/* Comparison grid — 9 rows, [metric · Pro avg · Reg avg · Lift] */}
      <div className="mt-5 rounded-xl border border-border overflow-hidden" data-testid="mkt-cohort-grid">
        <div className="grid grid-cols-12 text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/30 px-3 py-2">
          <div className="col-span-5">Metric</div>
          <div className="col-span-3 text-right">Pro avg</div>
          <div className="col-span-3 text-right">Regular avg</div>
          <div className="col-span-1 text-right">Lift</div>
        </div>
        {METRICS.map((m) => {
          const regV = pickCohortValue(reg, m.key);
          const proV = pickCohortValue(pro, m.key);
          const regTotal = pickCohortTotal(reg, m.key);
          const proTotal = pickCohortTotal(pro, m.key);
          const liftV = lift?.[m.key];
          // CTR row shows a small "clicks counted from {date}" note when
          // the window straddles the impression-tracking start date.
          const ctrClipped = m.key === "ctr_pct" && (pro?.ctr_window_clipped || reg?.ctr_window_clipped);
          const ctrStart = data?.meta?.ctr_window_start || data?.meta?.impression_tracking_start || null;
          return (
            <div
              key={m.key}
              className="grid grid-cols-12 items-center px-3 py-2.5 border-t border-border text-sm"
              data-testid={`mkt-row-${m.key}`}
            >
              <div className="col-span-5 flex items-center gap-2 min-w-0">
                <m.icon weight="duotone" className="h-4 w-4 text-primary flex-none" />
                <div className="min-w-0">
                  <div className="truncate font-medium">{m.label}</div>
                  {ctrClipped && ctrStart && (
                    <div className="text-[10px] text-amber-600 dark:text-amber-400 leading-tight" data-testid="mkt-ctr-clipped-note">
                      Clicks counted from {ctrStart} (tracking start)
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-3 text-right">
                <div className="tabular-nums font-semibold text-primary" data-testid={`mkt-pro-avg-${m.key}`}>
                  {loading ? "…" : fmt(proV, m.isPercent, m.isDays)}
                </div>
                {proTotal !== null && !loading && (
                  <div className="text-[10px] text-muted-foreground">total {proTotal}</div>
                )}
              </div>
              <div className="col-span-3 text-right">
                <div className="tabular-nums font-semibold" data-testid={`mkt-reg-avg-${m.key}`}>
                  {loading ? "…" : fmt(regV, m.isPercent, m.isDays)}
                </div>
                {regTotal !== null && !loading && (
                  <div className="text-[10px] text-muted-foreground">total {regTotal}</div>
                )}
              </div>
              <div className="col-span-1 text-right">
                <LiftBadge lift={liftV} loading={loading} testid={`mkt-lift-${m.key}`} />
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-[10px] text-muted-foreground leading-tight">
        Averages use only trucks with at least 1 engagement or posted event in the window — silent trucks don&apos;t drag the cohort down.
        Owner self-traffic and test accounts are excluded.
      </p>

      {/* Boost ROI — event-level "boosted vs non-boosted pin" comparison.
          Restricted to trucks that ran at least one boost in the window,
          so the comparison is apples-to-apples on the SAME trucks
          (boosted hours vs un-boosted hours). */}
      <BoostImpactTile data={boostImpact} loading={loading} />

      {/* Single-truck drilldown */}
      <div className="mt-7" data-testid="mkt-single-truck-section">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold text-lg">Drill into a single truck</h3>
          {selectedTruck && (
            <button
              type="button"
              onClick={() => setSelectedTruck(null)}
              className="text-[11px] text-muted-foreground hover:text-foreground"
              data-testid="mkt-clear-truck-btn"
            >
              Clear
            </button>
          )}
        </div>

        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-full h-11 px-3 rounded-xl border border-border bg-background flex items-center justify-between text-sm hover:border-primary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="mkt-truck-picker-btn"
              aria-expanded={pickerOpen}
            >
              <span className="flex items-center gap-2 min-w-0">
                <MagnifyingGlass weight="duotone" className="h-4 w-4 text-muted-foreground flex-none" />
                {selectedTruck ? (
                  <span className="truncate font-medium">
                    {selectedTruck.truck_name}
                    {selectedTruck.is_pro && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold text-primary">
                        <Sparkle weight="fill" className="h-2.5 w-2.5" />
                        Pro
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Search a truck to drill in…</span>
                )}
              </span>
              <CaretDown weight="bold" className="h-4 w-4 text-muted-foreground flex-none" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command shouldFilter>
              <CommandInput
                placeholder={`Search ${trucks.length} trucks…`}
                value={pickerQuery}
                onValueChange={setPickerQuery}
                data-testid="mkt-truck-picker-input"
              />
              <CommandList>
                <CommandEmpty>No trucks match.</CommandEmpty>
                {proTrucks.length > 0 && (
                  <CommandGroup heading={`PRO TRUCKS (${proTrucks.length})`}>
                    {proTrucks.map((t) => (
                      <CommandItem
                        key={t.truck_id}
                        value={`${t.truck_name} ${t.owner_email}`}
                        onSelect={() => {
                          setSelectedTruck(t);
                          setPickerOpen(false);
                          setPickerQuery("");
                        }}
                        data-testid={`mkt-truck-option-${t.truck_id}`}
                      >
                        <Sparkle weight="fill" className="h-3.5 w-3.5 text-primary mr-2 flex-none" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{t.truck_name}</div>
                          <div className="truncate text-[11px] text-muted-foreground">{t.owner_email}</div>
                        </div>
                        {selectedTruck?.truck_id === t.truck_id && (
                          <Check weight="bold" className="h-4 w-4 text-primary ml-2 flex-none" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {proTrucks.length > 0 && regTrucks.length > 0 && <CommandSeparator />}
                {regTrucks.length > 0 && (
                  <CommandGroup heading={`REGULAR TRUCKS (${regTrucks.length})`}>
                    {regTrucks.map((t) => (
                      <CommandItem
                        key={t.truck_id}
                        value={`${t.truck_name} ${t.owner_email}`}
                        onSelect={() => {
                          setSelectedTruck(t);
                          setPickerOpen(false);
                          setPickerQuery("");
                        }}
                        data-testid={`mkt-truck-option-${t.truck_id}`}
                      >
                        <span className="h-3.5 w-3.5 mr-2 flex-none rounded-full bg-muted" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{t.truck_name}</div>
                          <div className="truncate text-[11px] text-muted-foreground">{t.owner_email}</div>
                        </div>
                        {selectedTruck?.truck_id === t.truck_id && (
                          <Check weight="bold" className="h-4 w-4 text-primary ml-2 flex-none" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selectedTruck && (
          <SingleTruckPanel
            truck={selectedTruck}
            detail={truckDetail}
            loading={truckLoading}
          />
        )}
      </div>
    </section>
  );
}

function CohortHeaderTile({ cohort, label, tone, loading }) {
  const accent = tone === "primary";
  return (
    <div
      className={
        "rounded-xl border p-3 " +
        (accent ? "border-primary/30 bg-primary/5" : "border-border bg-background/50")
      }
      data-testid={`mkt-cohort-tile-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className={"text-[11px] uppercase tracking-wider font-semibold " + (accent ? "text-primary" : "text-muted-foreground")}>
          {label} trucks
          {accent && <Sparkle weight="fill" className="inline-block h-3 w-3 ml-1 -mt-0.5" />}
        </div>
        {!loading && cohort && (
          <span className="text-[10px] text-muted-foreground">
            {cohort.active_trucks_count}/{cohort.trucks_count} active
          </span>
        )}
      </div>
      <div className="font-display font-bold text-3xl tabular-nums leading-none mt-2">
        {loading ? "…" : (cohort?.trucks_count ?? 0)}
      </div>
    </div>
  );
}

function LiftBadge({ lift, loading, testid }) {
  if (loading) return <span className="text-[10px] text-muted-foreground" data-testid={testid}>…</span>;
  if (lift === null || lift === undefined) {
    return <span className="text-[10px] text-muted-foreground" data-testid={testid}>—</span>;
  }
  if (lift >= 9999) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400"
        data-testid={testid}
      >
        <Sparkle weight="fill" className="h-2.5 w-2.5" />
        NEW
      </span>
    );
  }
  if (lift === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground" data-testid={testid}>
        <Minus className="h-2.5 w-2.5" />
        0%
      </span>
    );
  }
  const up = lift > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  const color = up ? "text-emerald-600 dark:text-emerald-400" : "text-rose-500";
  return (
    <span
      className={"inline-flex items-center gap-0.5 text-[10px] font-semibold " + color}
      data-testid={testid}
    >
      <Icon weight="bold" className="h-2.5 w-2.5" />
      {Math.abs(lift)}%
    </span>
  );
}

function BoostImpactTile({ data, loading }) {
  // Hide entirely when no trucks boosted in the window — there's
  // literally nothing to compare. (Empty state would just be noise.)
  if (loading) {
    return (
      <div className="mt-6 rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground" data-testid="boost-impact-loading">
        Loading boost impact…
      </div>
    );
  }
  if (!data || data.trucks_boosted_count === 0) {
    return (
      <div className="mt-6 rounded-xl border border-dashed border-border p-4" data-testid="boost-impact-empty">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Lightning weight="duotone" className="h-4 w-4 text-amber-500" />
          Boost impact
        </div>
        <p className="text-[12px] text-muted-foreground mt-1">
          No boosts ran in this window. Once any Pro owner spends a boost slot, the boosted-vs-non-boosted CTR comparison shows up here.
        </p>
      </div>
    );
  }

  const b = data.boosted || {};
  const nb = data.non_boosted || {};
  const ctrLift = data.ctr_lift_pct;
  const liftIsPositive = typeof ctrLift === "number" && ctrLift > 0;
  const liftIsNegative = typeof ctrLift === "number" && ctrLift < 0;

  return (
    <section className="mt-6 rounded-xl border border-border bg-card" data-testid="boost-impact-tile">
      <header className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap">
        <Lightning weight="fill" className="h-4 w-4 text-amber-500" />
        <h3 className="font-display font-bold text-base tracking-tight">Boost impact</h3>
        <span className="text-[11px] text-muted-foreground">
          · {data.trucks_boosted_count} truck{data.trucks_boosted_count === 1 ? "" : "s"} ran{" "}
          {data.boost_runs_count} boost{data.boost_runs_count === 1 ? "" : "s"}
          {data.total_boost_hours ? ` · ${data.total_boost_hours}h boosted time` : ""}
        </span>
      </header>

      <div className="grid grid-cols-2 divide-x divide-border">
        {/* Boosted column */}
        <div className="p-4" data-testid="boost-impact-boosted">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-semibold">
            <Lightning weight="fill" className="h-3 w-3" /> During boost
          </div>
          <div className="mt-2 grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] text-muted-foreground">Impressions</div>
              <div className="tabular-nums font-semibold text-base">{b.pin_impression || 0}</div>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground">Clicks</div>
              <div className="tabular-nums font-semibold text-base">{b.pin_click || 0}</div>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground">CTR</div>
              <div className="tabular-nums font-semibold text-base text-amber-600 dark:text-amber-400">
                {b.ctr_pct == null ? "—" : `${b.ctr_pct}%`}
              </div>
            </div>
          </div>
        </div>

        {/* Non-boosted column */}
        <div className="p-4" data-testid="boost-impact-non-boosted">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            <Minus className="h-3 w-3" /> Outside boost
          </div>
          <div className="mt-2 grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] text-muted-foreground">Impressions</div>
              <div className="tabular-nums font-semibold text-base">{nb.pin_impression || 0}</div>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground">Clicks</div>
              <div className="tabular-nums font-semibold text-base">{nb.pin_click || 0}</div>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground">CTR</div>
              <div className="tabular-nums font-semibold text-base">
                {nb.ctr_pct == null ? "—" : `${nb.ctr_pct}%`}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTR lift summary line — the headline answer */}
      <div className="px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-between flex-wrap gap-2" data-testid="boost-impact-summary">
        <div className="text-[12px] text-muted-foreground">CTR lift while boosted vs. outside boost on the same trucks:</div>
        <div data-testid="boost-impact-ctr-lift">
          {ctrLift == null ? (
            <span className="text-[12px] text-muted-foreground">Not enough data to compare yet</span>
          ) : ctrLift >= 9999 ? (
            <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-600 dark:text-emerald-400">
              <Sparkle weight="fill" className="h-3 w-3" /> Boosted clicks only
            </span>
          ) : (
            <span
              className={
                "inline-flex items-center gap-1 text-[13px] font-semibold " +
                (liftIsPositive
                  ? "text-emerald-600 dark:text-emerald-400"
                  : liftIsNegative
                  ? "text-rose-500"
                  : "text-muted-foreground")
              }
            >
              {liftIsPositive ? (
                <ArrowUp weight="bold" className="h-3 w-3" />
              ) : liftIsNegative ? (
                <ArrowDown weight="bold" className="h-3 w-3" />
              ) : (
                <Minus className="h-3 w-3" />
              )}
              {ctrLift >= 0 ? "+" : ""}
              {ctrLift}%
            </span>
          )}
        </div>
      </div>

      <p className="px-4 pb-3 text-[10px] text-muted-foreground leading-tight">
        Event-level bucketing. Only trucks that ran ≥1 boost in the window are included, so this compares the same trucks' boosted hours to their own un-boosted hours.
      </p>
    </section>
  );
}

function SingleTruckPanel({ truck, detail, loading }) {
  const totals = detail?.totals || {};
  const series = detail?.daily_series || {};
  const dates = detail?.series_dates || [];
  const topDays = detail?.top_days || [];
  const ctr = detail?.ctr_pct;  // null when no impressions in window — fmt() renders "—"
  const ctrClipped = detail?.ctr_window_clipped;
  const ctrStart = detail?.impression_tracking_start;
  const postingDays = detail?.posting_days ?? 0;

  return (
    <div className="mt-4 rounded-xl border border-border p-4" data-testid="mkt-truck-detail">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-display font-bold text-lg truncate">{truck.truck_name}</h4>
            <span
              className={
                "inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full " +
                (truck.is_pro
                  ? "bg-primary/15 text-primary"
                  : "bg-muted text-muted-foreground")
              }
              data-testid="mkt-truck-detail-tier"
            >
              {truck.is_pro && <Sparkle weight="fill" className="h-2.5 w-2.5" />}
              {truck.is_pro ? "Pro" : "Regular"}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground truncate">{truck.owner_email}</div>
        </div>
      </div>

      {/* Metric tiles */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {METRICS.map((m) => {
          let v;
          if (m.key === "ctr_pct") v = ctr;
          else if (m.key === "avg_posting_days") v = postingDays;
          else v = totals[m.key] ?? 0;
          return (
            <div
              key={m.key}
              className="rounded-lg border border-border bg-background/50 p-2.5"
              data-testid={`mkt-truck-metric-${m.key}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">
                  {m.label}
                </div>
                <m.icon weight="duotone" className="h-3.5 w-3.5 text-primary" />
              </div>
              <div className="font-display font-bold text-xl tabular-nums leading-none mt-1.5">
                {loading ? "…" : fmt(v, m.isPercent, m.key === "avg_posting_days")}
              </div>
              {m.key === "ctr_pct" && ctrClipped && ctrStart && !loading && (
                <div className="mt-1 text-[9px] text-amber-600 dark:text-amber-400 leading-tight" data-testid="mkt-truck-ctr-clipped-note">
                  Clicks from {ctrStart}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Daily engagement bars (sum of all metrics per day) */}
      {!loading && dates.length > 0 && (
        <DailyBars dates={dates} series={series} />
      )}

      {/* Top engagement days — testid stays stable so tests can assert on it */}
      {!loading && (
        <div className="mt-4" data-testid="mkt-truck-top-days">
          <h5 className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
            Top engagement days
          </h5>
          {topDays.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {topDays.map((d) => (
                <div key={d.date} className="rounded-lg border border-border p-2.5 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {d.label}
                  </div>
                  <div className="font-display font-bold text-2xl tabular-nums mt-1">
                    {d.total}
                  </div>
                  <div className="text-[10px] text-muted-foreground">events</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-[11px] text-muted-foreground italic">
              No standout days yet — this truck hasn&apos;t received enough engagement in the window.
            </div>
          )}
        </div>
      )}

      {!loading && dates.length > 0 && totals.pin_impression === 0 && totals.pin_click === 0 && (
        <p className="mt-3 text-[11px] text-muted-foreground text-center italic">
          No engagement events recorded for this truck in the selected window.
        </p>
      )}
    </div>
  );
}

function DailyBars({ dates, series }) {
  // Sum across all metric event types per day → single bar.
  const byDay = dates.map((d, i) => {
    let sum = 0;
    for (const k of Object.keys(series || {})) {
      sum += (series[k]?.[i] || 0);
    }
    return { date: d, sum };
  });
  const max = Math.max(1, ...byDay.map((d) => d.sum));
  return (
    <div className="mt-4" data-testid="mkt-truck-daily-bars">
      <h5 className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
        Daily engagement
      </h5>
      <div className="flex items-end gap-1 h-24">
        {byDay.map((d) => {
          const h = (d.sum / max) * 100;
          return (
            <div
              key={d.date}
              className="flex-1 flex flex-col items-stretch group"
              title={`${d.date} · ${d.sum} events`}
            >
              <div className="flex-1 flex items-end">
                <div
                  className={"w-full rounded-t " + (d.sum > 0 ? "bg-primary" : "bg-muted")}
                  style={{ height: `${Math.max(2, h)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
        <span>{dates[0]}</span>
        <span>{dates[dates.length - 1]}</span>
      </div>
    </div>
  );
}
